import deepmerge from "deepmerge";
import { AbstractManager } from "./AbstractManager.js";
import { isDev, isFunction, isDefined, isBoolean, isArray, isObject } from "../../utils/index.js";
const types = /* @__PURE__ */ new Set([String, Number, Boolean, Array, Object]);
const __defaultValues = {
  String: "",
  Number: 0,
  Boolean: false,
  Array: () => [],
  Object: () => ({})
};
const __propertyNameCache = /* @__PURE__ */ new Map();
function __getPropertyName(name, prefix = "") {
  const key = name + prefix;
  if (__propertyNameCache.has(key)) {
    return __propertyNameCache.get(key);
  }
  const propertyName = `option${prefix}${name.replace(/^\w/, (c) => c.toUpperCase())}`;
  __propertyNameCache.set(key, propertyName);
  return propertyName;
}
function __register(that, name, config) {
  if (!types.has(config.type)) {
    if (isDev) {
      throw new Error(
        `The "${name}" option has an invalid type. The allowed types are: String, Number, Boolean, Array and Object.`
      );
    }
    return;
  }
  config.default = config.default ?? __defaultValues[config.type.name];
  if ((config.type === Array || config.type === Object) && !isFunction(config.default)) {
    if (isDev) {
      throw new Error(
        `The default value for options of type "${config.type.name}" must be returned by a function.`
      );
    }
    return;
  }
  Object.defineProperty(that, name, {
    get: () => that.get(name, config),
    set: (value) => {
      that.set(name, value, config);
    },
    enumerable: true
  });
}
class OptionsManager extends AbstractManager {
  /**
   * An object to store Array and Object values for reference.
   *
   * @private
   */
  __values = {};
  /**
   * Default value for the name property.
   */
  name = "Base";
  /**
   * Default value for the debug property.
   */
  debug = false;
  /**
   * Default value for the log property.
   */
  log = false;
  /**
   * Class constructor.
   */
  constructor(base) {
    super(base);
    this.__hideProperties(["__values", "__defaultValues"]);
    const schema = this.__config.options || {};
    this.name = this.__config.name;
    schema.debug = {
      type: Boolean,
      default: this.__config.debug ?? false
    };
    schema.log = {
      type: Boolean,
      default: this.__config.log ?? false
    };
    for (const [name, config] of Object.entries(schema)) {
      __register(
        this,
        name,
        types.has(config) ? { type: config } : config
      );
    }
  }
  /**
   * Get an option value.
   */
  get(name, config) {
    const { type, default: defaultValue } = config;
    const propertyName = __getPropertyName(name);
    const hasProperty = isDefined(this.__element.dataset[propertyName]);
    if (type === Boolean) {
      if (defaultValue) {
        const negatedPropertyName = __getPropertyName(name, "No");
        const hasNegatedProperty = isDefined(this.__element.dataset[negatedPropertyName]);
        return !hasNegatedProperty;
      }
      return hasProperty || defaultValue;
    }
    const value = this.__element.dataset[propertyName];
    if (type === Number) {
      return hasProperty ? Number(value) : defaultValue;
    }
    if (type === Array || type === Object) {
      config = type === Array ? config : config;
      if (!this.__values[name]) {
        let val = hasProperty ? JSON.parse(value) : config.default();
        if (isDefined(config.merge)) {
          val = isBoolean(config.merge) ? deepmerge(config.default(), val) : deepmerge(config.default(), val, config.merge);
        }
        this.__values[name] = val;
      }
      return this.__values[name];
    }
    return hasProperty ? value : defaultValue;
  }
  /**
   * Set an option value.
   */
  set(name, value, config) {
    const { type, default: defaultValue } = config;
    const propertyName = __getPropertyName(name);
    if (value.constructor.name !== type.name) {
      if (isDev) {
        const val = isArray(value) || isObject(value) ? JSON.stringify(value) : value;
        throw new TypeError(
          `The "${val}" value for the "${name}" option must be of type "${type.name}"`
        );
      }
      return;
    }
    switch (type) {
      case Boolean:
        if (defaultValue) {
          const negatedPropertyName = __getPropertyName(name, "No");
          if (value) {
            delete this.__element.dataset[negatedPropertyName];
          } else {
            this.__element.dataset[negatedPropertyName] = "";
          }
        } else if (value) {
          this.__element.dataset[propertyName] = "";
        } else {
          delete this.__element.dataset[propertyName];
        }
        break;
      case Array:
      case Object:
        this.__values[name] = value;
        break;
      default:
        this.__element.dataset[propertyName] = value;
    }
  }
}
export {
  OptionsManager,
  __getPropertyName
};
//# sourceMappingURL=OptionsManager.js.map
